import { useContext, useMemo } from 'react';
import SavedSearchesContext from '~/context/common/SavedSearchesContext';
import RecentSearchesContext from '~/context/home/RecentSearchesContext';
import { SavedSearch } from '~/contracts/common';
import { RecentSearch } from '~/contracts/home';
import useGetSuggestedSearches, {
  SuggestedSearch
} from './useGetSuggestedSearches';

export enum SRPLinkTitles {
  Saved = 'Saved searches',
  Recent = 'Recent searches',
  Suggested = 'Suggested searches',
  None = ''
}

type HookReturnType = {
  header: SRPLinkTitles;
  searches: SavedSearch[] | RecentSearch[] | SuggestedSearch[];
  showLoading: boolean;
  showLink?: boolean;
};

export default function useGetSRPLinksToShow(): HookReturnType {
  const {
    savedSearches: { data: savedSearches, isLoaded: isSavedSearchesLoaded }
  } = useContext(SavedSearchesContext);
  const {
    recentSearches: { data: recentSearches, isLoaded: isRecentSeachesLoaded }
  } = useContext(RecentSearchesContext);

  const suggestedSearches = useGetSuggestedSearches();
  const allLoaded = isSavedSearchesLoaded && isRecentSeachesLoaded;
  const hasSavedSearches = savedSearches.length > 0;
  const hasRecentSearches = recentSearches.length > 0;

  return useMemo(() => {
    if (!allLoaded) {
      return { header: SRPLinkTitles.None, searches: [], showLoading: true };
    }

    if (hasSavedSearches) {
      return {
        header: SRPLinkTitles.Saved,
        searches: savedSearches.slice(0, 4),
        showLoading: false,
        showLink: savedSearches.length > 4
      };
    }

    if (!hasSavedSearches && hasRecentSearches) {
      return {
        header: SRPLinkTitles.Recent,
        searches: recentSearches.slice(0, 4),
        showLoading: false
      };
    }

    return {
      header: SRPLinkTitles.Suggested,
      searches: suggestedSearches,
      showLoading: false
    };
  }, [
    allLoaded,
    hasSavedSearches,
    hasRecentSearches,
    savedSearches,
    recentSearches,
    suggestedSearches
  ]);
}
